import React from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { useLang } from "@/lib/i18n";

const CONTENT = {
  it: {
    title: "Termini di Servizio",
    updated: "Ultimo aggiornamento: 27 agosto 2026",
    back: "Torna alla home",
    sections: [
      ["1. Cos'è Snetch", "Snetch è una piattaforma di skill game a tema trading: i giocatori competono su un grafico finanziario cercando di ottenere il maggior profitto in una partita a tempo. L'esito dipende dall'abilità del giocatore, non dal caso."],
      ["2. Account", "Per giocare devi registrare un account con un'email valida. Sei responsabile della riservatezza delle tue credenziali e di tutte le attività svolte dal tuo account."],
      ["3. Crediti, buy-in e montepremi", "Le partite possono prevedere un buy-in in crediti. Il montepremi è formato dagli ingressi al netto di una commissione del sito (20%). I crediti demo non hanno valore monetario. Solo le vincite derivanti dal gioco sono eventualmente prelevabili, secondo le regole indicate nell'app."],
      ["4. Condotta", "È vietato usare bot, exploit, account multipli o qualsiasi mezzo che alteri l'equità del gioco. Possiamo sospendere gli account che violano queste regole."],
      ["5. Proprietà intellettuale", "Il nome \"Snetch\", il logo, il software, la grafica e i contenuti sono di proprietà del titolare della piattaforma e protetti dal diritto d'autore. Nessun diritto ti viene ceduto salvo l'uso personale del servizio."],
      ["6. Nessuna garanzia", "Il servizio è fornito \"così com'è\". Non garantiamo continuità del servizio, assenza di errori o risultati specifici. Snetch non è consulenza finanziaria."],
      ["7. Limitazione di responsabilità", "Nei limiti di legge, non siamo responsabili per danni indiretti o perdite derivanti dall'uso del servizio."],
      ["8. Modifiche e legge applicabile", "Possiamo aggiornare questi termini; le modifiche saranno pubblicate su questa pagina. I presenti termini sono regolati dalla legge italiana."],
      ["9. Contatti", "Per qualsiasi richiesta: support@playsnetch.com"],
    ],
  },
  en: {
    title: "Terms of Service",
    updated: "Last updated: August 27, 2026",
    back: "Back to home",
    sections: [
      ["1. What Snetch is", "Snetch is a trading-themed skill-game platform: players compete on a financial chart trying to achieve the highest profit within a timed match. The outcome depends on player skill, not chance."],
      ["2. Account", "You must register an account with a valid email to play. You are responsible for keeping your credentials confidential and for all activity under your account."],
      ["3. Credits, buy-ins and prize pool", "Matches may require a credit buy-in. The prize pool is formed by entries net of a 20% site fee. Demo credits have no monetary value. Only winnings generated from gameplay may be withdrawable, per the rules shown in the app."],
      ["4. Conduct", "Bots, exploits, multiple accounts or any means that undermine fair play are forbidden. We may suspend accounts that violate these rules."],
      ["5. Intellectual property", "The \"Snetch\" name, logo, software, graphics and content are owned by the platform holder and protected by copyright. No rights are granted to you except personal use of the service."],
      ["6. No warranty", "The service is provided \"as is\". We do not guarantee uptime, error-free operation or specific results. Snetch is not financial advice."],
      ["7. Limitation of liability", "To the extent permitted by law, we are not liable for indirect damages or losses arising from use of the service."],
      ["8. Changes and governing law", "We may update these terms; changes will be posted on this page. These terms are governed by Italian law."],
      ["9. Contact", "For any request: support@playsnetch.com"],
    ],
  },
};

export default function Terms() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.it;
  return <LegalPage c={c} testid="terms-page" />;
}

export function LegalPage({ c, testid }) {
  return (
    <div className="min-h-screen bg-base text-white" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-5 py-12">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Activity className="w-6 h-6 text-signal" strokeWidth={2.5} />
          <span className="font-heading font-black text-xl tracking-tight">SNETCH</span>
        </Link>
        <h1 className="font-heading font-black text-4xl tracking-tight mb-2">{c.title}</h1>
        <div className="w-12 h-1 bg-signal mb-3" />
        <p className="font-mono text-xs text-white/40 mb-10">{c.updated}</p>
        <div className="space-y-8">
          {c.sections.map(([h, p], i) => (
            <section key={h}>
              <h2 className="font-heading font-bold text-lg mb-2">{h}</h2>
              <p className="text-white/60 text-sm leading-relaxed">{p}</p>
            </section>
          ))}
        </div>
        <Link to="/" className="mt-12 inline-block font-mono text-sm text-signal hover:text-white transition-colors">
          ← {c.back}
        </Link>
      </div>
    </div>
  );
}
